import Sveip from '@nrk/dh-basis-sveip'
import { closest, connectScrollContainerLazyLoad } from '@nrk/dh-basis-common'
import { remToPx } from '../../lib/remToPx'
import { toArray } from '@nrk/dh-utils'
import { debounce } from '../../lib/utils'
import styles from './bildesveip.module.css'

export default class Bildesveip extends Sveip {
  #offsetRules = [
    {
      name: 'XL-full-width',
      isMatch(sveip) {
        return (
          closest(sveip, 'article')?.classList?.contains('article-feature') &&
          closest(sveip, '.remoterenderedcontent-reference')?.classList.contains('fc')
        )
      },
      calculateOffset() {
        if (window.innerWidth > 900) {
          const leftPadding = remToPx(17.5) - 0.025 * window.innerWidth
          return { left: `${(-1 * leftPadding).toFixed(2)}px` }
        } else {
          return {}
        }
      },
    },
  ]
  connectedCallback() {
    super.connectedCallback()
    this.updateOffset()
    this.debouncedUpdateSizes = debounce(this.updateSizes, { delay: 200 })

    window.addEventListener('resize', this.updateOffset)
    window.addEventListener('resize', this.debouncedUpdateSizes)
    window.addEventListener('load', this.debouncedUpdateSizes)
    this.mediaElements.forEach((img) => {
      img.addEventListener('load', this.debouncedUpdateSizes)
    })

    this._disconnectLazyLoad = connectScrollContainerLazyLoad(
      this.scrollContainer,
      this.querySelectorAll('img')
    )

    setTimeout(() => {
      this.debouncedUpdateSizes()
      this.setStartIndex(this.calculateStartIndex())
    }, 100)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('resize', this.updateOffset)
    window.removeEventListener('resize', this.debouncedUpdateSizes)
    window.removeEventListener('load', this.debouncedUpdateSizes)
    this.mediaElements.forEach((img) => {
      img.removeEventListener('load', this.debouncedUpdateSizes)
    })
    this._disconnectLazyLoad()
  }

  updateOffset = () => {
    const rule = this.#offsetRules?.find((r) => r.isMatch(this))
    this.offset = rule?.calculateOffset() ?? {}
  }

  calculateStartIndex() {
    if (this.startIndex !== 'auto') {
      return parseInt(this.startIndex)
    }

    const offset = this.offset

    // Calculate width of left half of container, including scroll padding
    const containerWidth = this.scrollContainer.offsetWidth + offset.left + offset.right
    const containerLeftHalf = containerWidth / 2 - offset.left

    const items = this.items
    const firstImage = items[0]
    const secondImage = items[1]

    if (
      this.items.length >= 3 &&
      containerLeftHalf > firstImage.offsetWidth + secondImage.offsetWidth / 2
    ) {
      // Container is wide enough to show first image, when second image is selected
      return 1
    } else {
      return 0
    }
  }

  getImageMaxHeight() {
    return (
      this.mediaElements.reduce(
        (acc, img) => Math.max(acc, img.getBoundingClientRect().height),
        0
      ) + 1
    )
  }

  updateSizes = () => {
    const componentWidth = this.offsetWidth
    this.style.setProperty('--bs-component-width', `${componentWidth}px`)

    const imageMaxHeight = this.getImageMaxHeight()

    if (imageMaxHeight > 20) {
      this.style.setProperty('--bs-img-max-height', `${imageMaxHeight}px`)
    }

    this.setAspectRatioVars()
    this.setImageCentering(imageMaxHeight)
  }

  /**
   * Calculates the vertical top offset for each image.
   * Used to set margin top on each image element.
   */
  setImageCentering(maxHeight = 0) {
    this.mediaElements.forEach((el) => {
      const rect = el.getBoundingClientRect()
      const value = Math.max((maxHeight - rect.height) / 2, 0)
      // @ts-ignore
      el.style.setProperty('margin-top', `${value}px`)
    })
  }

  /**
   * Updates the aspect ratio variables.
   * The image aspect ratios need to be adjusted based on screen size and.
   * whether or not the set of images contain a mix of very different aspects.
   */
  setAspectRatioVars() {
    if (typeof window !== 'undefined' && window.innerWidth > 720) {
      this.style.setProperty('--bs-mixed-landscape-aspect', '1.3')
      this.style.setProperty('--bs-mixed-vertical-aspect', '0.6')
      this.style.setProperty('--bs-mixed-equal-aspect', '0.8')
    } else {
      this.style.setProperty('--bs-mixed-landscape-aspect', '1')
      this.style.setProperty('--bs-mixed-vertical-aspect', '0.6')
    }
  }

  get mediaElements() {
    return toArray(this.items).map((item) => item.querySelector(`img, .${styles.media}`))
  }

  set offsetRules(val) {
    this.#offsetRules = val
    this.updateOffset()
  }

  get offsetRules() {
    return this.#offsetRules
  }

  set startIndex(val) {
    this.setAttribute('start-index', val)
  }

  get startIndex() {
    return this.getAttribute('start-index') ?? 'auto'
  }
}
